import React, { useEffect, useRef } from 'react';
import { RelBox } from '../../../api/ai.api.types';
import { rampColor } from '../helper/alignmentGuidance';

interface AlignmentMetaballsProps {
    box: RelBox | null;
    // The FIXED outer target frame ("soll"-Rahmen). Each corner of the detected (usually smaller,
    // centered) box connects to the corresponding corner of this frame — inside → outside.
    targetBox: RelBox;
    score: number;
}

// Classic metaball ("gooey") effect: soft-blurred circles pushed back to hard alpha edges via
// feColorMatrix, so nearby blobs organically merge. Each corner of the detected meter box is
// connected to the corresponding corner of the fixed target frame by a CONTINUOUS strand: a
// dense chain of blobs whose radii thin toward the middle (waist), so the goo filter fuses them
// into one stretchy tentacle. The chain positions drift inside → outside, giving the strand a
// liquid flow that shows the meter "wanting" to reach the frame. Everything lives in ONE svg
// because Safari only supports filter:url(...) on SVG elements, not on HTML/canvas. Circles are
// animated via direct attribute writes in a rAF loop — React never re-renders during the
// animation. Honors prefers-reduced-motion by rendering nothing.
// Sizing note: the goo filter blurs the circles and then hard-thresholds the alpha — a circle
// only survives that when its radius is comfortably LARGER than the blur. All sizes below are
// BASE values for a meter box roughly as large as the target frame; per frame everything
// (radii, spacing, wobble, AND the blur itself) is multiplied by a scale derived from the
// detected meter-box size, so the goo grows as the user moves closer and stays fine-grained at
// a distance — with a constant radius/blur ratio the threshold behaves identically at any scale.
const MAX_CHAIN_BLOBS = 16;
// One full inside→outside drift cycle of the chain.
const FLOW_DURATION_MS = 1400;
const ANCHOR_RADIUS_METER = 16;
const ANCHOR_RADIUS_TARGET = 13;
const CHAIN_RADIUS = 12;
// Approximate spacing between chain blobs (px) — must stay near/below the blob radius so the
// blurred alpha fields overlap and the goo filter fuses the chain into one strand.
const CHAIN_SPACING_PX = 16;
// Below this corner distance (in px) the strand is considered collapsed (aligned) — the chain
// hides and the anchors pulse instead.
const COLLAPSED_DISTANCE_PX = 30;
const BASE_BLUR = 8;
// Meter-box diagonal relative to the target-frame diagonal → effect scale, clamped so the goo
// neither vanishes when the meter is tiny nor floods the screen when it fills the frame.
const SIZE_SCALE_MIN = 0.4;
const SIZE_SCALE_MAX = 1.5;

const easeInOutCubic = (t: number): number => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);

const corners = (box: RelBox): Array<{ x: number; y: number }> => [
    { x: box.x, y: box.y },
    { x: box.x + box.w, y: box.y },
    { x: box.x, y: box.y + box.h },
    { x: box.x + box.w, y: box.y + box.h },
];

const AlignmentMetaballs = ({ box, targetBox, score }: AlignmentMetaballsProps) => {
    const svgRef = useRef<SVGSVGElement>(null);
    const groupRef = useRef<SVGGElement>(null);
    const blurRef = useRef<SVGFEGaussianBlurElement>(null);
    const circleRefs = useRef<Array<SVGCircleElement | null>>([]);
    const lastBlurScaleRef = useRef(1);

    const boxRef = useRef(box);
    boxRef.current = box;
    const targetBoxRef = useRef(targetBox);
    targetBoxRef.current = targetBox;
    const scoreRef = useRef(score);
    scoreRef.current = score;

    useEffect(() => {
        const reducedMotion = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const svg = svgRef.current;
        if (!svg || reducedMotion) {
            return undefined;
        }

        let cancelled = false;
        let rafHandle = 0;
        let width = svg.clientWidth || 1;
        let height = svg.clientHeight || 1;
        const resize = () => {
            width = svg.clientWidth || 1;
            height = svg.clientHeight || 1;
        };
        window.addEventListener('resize', resize);

        const setCircle = (index: number, x: number, y: number, r: number) => {
            const circle = circleRefs.current[index];
            circle?.setAttribute('cx', x.toFixed(1));
            circle?.setAttribute('cy', y.toFixed(1));
            circle?.setAttribute('r', Math.max(0, r).toFixed(1));
        };

        const tick = (now: number) => {
            if (cancelled) {
                return;
            }
            rafHandle = window.requestAnimationFrame(tick);

            groupRef.current?.setAttribute('fill', rampColor(scoreRef.current));

            const meter = boxRef.current;
            if (!meter) {
                // No meter — hide everything.
                for (let i = 0; i < circleRefs.current.length; i++) {
                    setCircle(i, 0, 0, 0);
                }
                return;
            }

            const meterCorners = corners(meter);
            const targetCorners = corners(targetBoxRef.current);

            // Effect scale follows the on-screen meter size: closer camera → bigger meter box →
            // fatter goo. The blur scales along so the threshold behavior stays identical.
            const meterDiagonal = Math.hypot(meter.w * width, meter.h * height);
            const targetDiagonal = Math.hypot(targetBoxRef.current.w * width, targetBoxRef.current.h * height);
            const scale = Math.min(SIZE_SCALE_MAX, Math.max(SIZE_SCALE_MIN, targetDiagonal > 1e-3 ? meterDiagonal / targetDiagonal : 1));
            if (Math.abs(scale - lastBlurScaleRef.current) > 0.03) {
                lastBlurScaleRef.current = scale;
                blurRef.current?.setAttribute('stdDeviation', (BASE_BLUR * scale).toFixed(2));
            }

            let index = 0;
            for (let c = 0; c < 4; c++) {
                const from = { x: meterCorners[c].x * width, y: meterCorners[c].y * height };
                const to = { x: targetCorners[c].x * width, y: targetCorners[c].y * height };
                const distance = Math.hypot(to.x - from.x, to.y - from.y);
                const collapsed = distance < COLLAPSED_DISTANCE_PX * scale;
                const pulse = collapsed ? 1 + 0.2 * Math.sin(now / 180 + c) : 1;

                setCircle(index++, from.x, from.y, ANCHOR_RADIUS_METER * scale * pulse);
                setCircle(index++, to.x, to.y, ANCHOR_RADIUS_TARGET * scale * pulse);

                // Continuous strand: enough chain blobs for the goo filter to fuse them into one
                // tentacle; the count adapts to the corner distance.
                const chainCount = collapsed ? 0 : Math.min(MAX_CHAIN_BLOBS, Math.max(3, Math.round(distance / (CHAIN_SPACING_PX * scale))));
                const flowPhase = (now / FLOW_DURATION_MS + c * 0.25) % 1;
                const nx = distance > 1e-3 ? -(to.y - from.y) / distance : 0;
                const ny = distance > 1e-3 ? (to.x - from.x) / distance : 0;

                for (let j = 0; j < MAX_CHAIN_BLOBS; j++) {
                    if (j >= chainCount) {
                        setCircle(index++, to.x, to.y, 0);
                        continue;
                    }
                    // Evenly spaced along the strand, drifting inside → outside; the modulo makes
                    // blobs respawn at the meter corner as they reach the frame corner.
                    const t = (j / chainCount + flowPhase) % 1;
                    const eased = easeInOutCubic(t);
                    // Subtle perpendicular wobble keeps the strand organic instead of ruler-straight.
                    const wobble = Math.sin(t * Math.PI * 2 + now / 500 + c) * Math.min(8 * scale, distance * 0.05);
                    const x = from.x + (to.x - from.x) * eased + nx * wobble;
                    const y = from.y + (to.y - from.y) * eased + ny * wobble;
                    // Waist profile: thick near both anchors, thinnest mid-strand — the signature
                    // stretchy-goo silhouette. The waist must stay above the filter's survival
                    // radius (see sizing note above) or the strand visually tears in the middle.
                    const waist = 0.7 + 0.3 * Math.abs(2 * t - 1);
                    setCircle(index++, x, y, CHAIN_RADIUS * scale * waist);
                }
            }
        };

        rafHandle = window.requestAnimationFrame(tick);
        return () => {
            cancelled = true;
            window.cancelAnimationFrame(rafHandle);
            window.removeEventListener('resize', resize);
        };
    }, []);

    const circleCount = 4 * (2 + MAX_CHAIN_BLOBS);

    return (
        <svg ref={svgRef} data-testid="alignment-metaballs" className={'absolute inset-0 w-full h-full pointer-events-none'} aria-hidden={true}>
            <defs>
                {/* Gooey/metaball filter: blur widens the alpha field, the color matrix hardens it
                    back — overlapping soft circles merge into organic blobs. */}
                <filter id="aiReadingGoo" x="-30%" y="-30%" width="160%" height="160%">
                    <feGaussianBlur ref={blurRef} in="SourceGraphic" stdDeviation={BASE_BLUR} result="blur" />
                    <feColorMatrix in="blur" mode="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 24 -10" />
                </filter>
            </defs>
            <g ref={groupRef} filter="url(#aiReadingGoo)" opacity="0.85">
                {Array.from({ length: circleCount }, (_, i) => (
                    <circle
                        key={i}
                        ref={(el) => {
                            circleRefs.current[i] = el;
                        }}
                        cx={0}
                        cy={0}
                        r={0}
                    />
                ))}
            </g>
        </svg>
    );
};

export default AlignmentMetaballs;
